// src/pages/ShowCart.jsx
import React, { useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./ShowCart.css";
import Footer from "./footer";
import Spinner from "../components/Spinner";

const API_BASE = "https://api.goharesadaf.ir";
const GUEST_KEY = "guestCart";

export default function ShowCart() {
  const [items, setItems] = useState([]); // normalized cart items
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------- helpers --------------------

  const readGuestRaw = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(GUEST_KEY) || "[]");
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      console.warn("failed to parse guestCart", e);
      return [];
    }
  };

  const saveGuestRaw = (rawArray) => {
    try {
      // store in stable shape: { id, product, quantity }
      const toStore = rawArray.map((r) => {
        // r might already be { id, product, quantity } or a product object
        if (r.product) {
          return { id: r.id ?? r.product.id, product: r.product, quantity: r.quantity ?? 1 };
        }
        // if r looks like product object (has title)
        const prod = r.product || r;
        return { id: prod.id, product: prod, quantity: r.quantity ?? prod.quantity ?? 1 };
      });
      localStorage.setItem(GUEST_KEY, JSON.stringify(toStore));
    } catch (e) {
      console.error("saveGuestRaw failed", e);
    }
  };

  // normalize a raw entry to internal item shape
  const normalizeRawItem = (raw) => {
    // Cases handled:
    // 1) raw = { id, product: {...}, quantity }
    // 2) raw = { id, title, price, ... , quantity? }  (product object itself)
    // 3) backend item: { id: cart_item_id, product: {...}, quantity }
    // 4) other variants
    const hasProductField = raw && typeof raw.product === "object";
    const looksLikeProduct = raw && raw.id && raw.title;
    const isBackendCartRow = raw && raw.product && raw.id && raw.quantity != null; // typical backend row

    if (hasProductField) {
      const product = raw.product;
      return {
        cartItemId: raw.id ?? null,
        productId: product.id ?? raw.id ?? null,
        product,
        quantity: Number(raw.quantity ?? 1),
        isGuest: !token,
      };
    }

    if (looksLikeProduct) {
      // raw is the product object (guest)
      return {
        cartItemId: null,
        productId: raw.id,
        product: raw,
        quantity: Number(raw.quantity ?? 1),
        isGuest: !token,
      };
    }

    // fallback
    return {
      cartItemId: raw.id ?? null,
      productId: raw.product_id ?? raw.productId ?? raw.id ?? null,
      product: raw.product ?? raw,
      quantity: Number(raw.quantity ?? 1),
      isGuest: !token,
    };
  };

  const normalizeArray = (arr) => (arr || []).map((r) => normalizeRawItem(r));

  // -------------------- fetch cart --------------------

  const fetchCart = async () => {
    setLoading(true);
    setError(null);

    if (!token) {
      // guest: read localStorage and normalize
      const raw = readGuestRaw();
      console.debug("ShowCart: raw guestCart read:", raw);
      const normalized = normalizeArray(raw);
      setItems(normalized);
      setLoading(false);
      return;
    }

    // logged-in user
    try {
      const res = await fetch(`${API_BASE}/api/cart`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to fetch cart");
      }
      const data = await res.json();
      // data may be { items: [...] } or array
      const rawItems = Array.isArray(data) ? data : data.items ?? [];
      console.debug("ShowCart: backend cart raw:", rawItems);
      setItems(normalizeArray(rawItems));
    } catch (err) {
      console.error("fetchCart error:", err);
      setError(err.message || "خطا در دریافت سبد خرید");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- mutations --------------------

  const removeGuestItemByProductId = (productId) => {
    const raw = readGuestRaw();
    const updatedRaw = raw.filter((r) => {
      const pid = r.product?.id ?? r.id ?? null;
      return pid !== productId;
    });
    saveGuestRaw(updatedRaw);
    setItems(normalizeArray(updatedRaw));
  };

  const handleRemove = async (it) => {
    // it is normalized item
    if (!token || it.isGuest) {
      removeGuestItemByProductId(it.productId);
      return;
    }

    // logged-in -> ask backend to remove cart row
    try {
      const res = await fetch(`${API_BASE}/api/cart/remove`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ item_id: it.cartItemId ?? it.productId }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "خطا در حذف محصول");
      }
      await fetchCart();
    } catch (err) {
      alert(err.message || "خطا در حذف محصول");
    }
  };

  const handleQuantityChange = async (it, newQty) => {
    if (newQty < 1) return;
    if (!token || it.isGuest) {
      // update guest localStorage
      const raw = readGuestRaw();
      const updatedRaw = raw.map((r) => {
        const pid = r.product?.id ?? r.id ?? null;
        if (pid === it.productId) {
          // ensure stored shape { id, product, quantity }
          const product = r.product ?? r;
          return { id: product.id, product, quantity: newQty };
        }
        return r;
      });
      saveGuestRaw(updatedRaw);
      setItems(normalizeArray(updatedRaw));
      return;
    }

    // logged-in: call backend update
    try {
      const res = await fetch(`${API_BASE}/api/cart/update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ item_id: it.cartItemId ?? it.productId, quantity: newQty }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "خطا در بروزرسانی تعداد");
      }
      await fetchCart();
    } catch (err) {
      alert(err.message || "خطا در بروزرسانی تعداد");
    }
  };

  // -------------------- checkout & utils --------------------

  const calculateTotal = () => {
    return items.reduce((sum, it) => {
      const price = Number(it.product?.price ?? it.price ?? 0) || 0;
      const qty = Number(it.quantity ?? 0) || 0;
      return sum + price * qty;
    }, 0);
  };

  const handleCheckout = () => {
  if (!token) {
    alert("لطفاً ابتدا وارد حساب کاربری شوید.");
    navigate(`/auth?redirect=${encodeURIComponent("/cart")}`);
    return;
  }
  if (!items.length) {
    alert("سبد خرید خالی است.");
    return;
  }
  navigate("/checkout");
};


  // -------------------- render --------------------

  if (!items.length) {
    return (
      <div className="cart-page">
              {loading && <Spinner />}
              {error && <p>خطا در بارگذاری: {error}</p>}
        <h1 className="cart-title">سبد خرید</h1>
        <div className="cart-empty">
          <img src="/images/cartEmpty.png" alt="سبد خرید خالی است" className="empty-cart-image" />
          <p>سبد خرید شما خالی است!</p>
          <button onClick={() => navigate("/products")} className="start-shopping">
            شروع خرید
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <h1 className="cart-title">سبد خرید</h1>

      <div className="cart-items">
        {items.map((it) => {
          const product = it.product || {};
          const imgSrc = product.main_image_url
            ? product.main_image_url
            : product.main_image
            ? product.main_image.startsWith("http")
              ? product.main_image
              : `${API_BASE}/storage/${product.main_image}`
            : "/images/placeholder.png";

          const unitPrice = Number(product.price ?? it.price ?? 0);

          return (
            <div className="cart-item" key={it.productId ?? it.cartItemId ?? product.id}>
              <img
                src={imgSrc}
                alt={product.title || "product"}
                className="cart-item-image"
                loading="lazy"
                onError={(e) => (e.currentTarget.src = "/images/placeholder.png")}
              />
              <div className="cart-item-details">
                <h3>{product.title || "محصول"}</h3>
                <p>قیمت واحد: {unitPrice.toLocaleString("fa-IR")} تومان</p>
                <div className="cart-quantity">
                  <button onClick={() => handleQuantityChange(it, it.quantity - 1)}>-</button>
                  <span>{(it.quantity ?? 0).toLocaleString("ar-EG")}</span>
                  <button onClick={() => handleQuantityChange(it, it.quantity + 1)}>+</button>
                </div>
                <p>مجموع: {(unitPrice * (it.quantity ?? 0)).toLocaleString("fa-IR")} تومان</p>
              </div>
              <button className="cart-remove-btn" onClick={() => handleRemove(it)} aria-label="حذف">
                <FiTrash2 />
              </button>
            </div>
          );
        })}
      </div>

      <div className="cart-total">
        <h2>جمع کل: {calculateTotal().toLocaleString("fa-IR")} تومان</h2>

        <div className="cart-actions" style={{ marginTop: 12, display: "flex", gap: 12 }}>
          <button className="checkout-button" onClick={handleCheckout} disabled={checkoutLoading}>
            {checkoutLoading ? "در حال پردازش..." : "تکمیل سفارش"}
          </button>

          <button className="continue-shopping" onClick={() => navigate("/products")}>
            ادامه خرید
          </button>
        </div>
      </div>

      <div className="footer-wrapper">
        <Footer />
      </div>
    </div>

    

  );
}
